// Bilingual translations for Orion FM website
// Edit any string here to update content site-wide

export const translations = {
  en: {
    // Navigation
    nav: {
      home: "Home",
      about: "About",
      services: "Services",
      industries: "Industries",
      amc: "AMC & SLA",
      blog: "Blog",
      careers: "Careers",
      contact: "Contact",
      getQuote: "Get Free Quote",
    },

    // Common
    common: {
      whatsapp: "WhatsApp Now",
      callNow: "Call Now",
      learnMore: "Learn More",
      readMore: "Read More",
      viewAll: "View All",
      requestQuote: "Request a Quote",
      bookConsultation: "Book Consultation",
      submitting: "Submitting…",
      successTitle: "Request received",
      successMsg: "Our team will reach out within 1 business hour.",
      errorMsg: "Something went wrong. Please try again or WhatsApp us.",
      requiredField: "Required",
      selectService: "Select a service",
      cities: "Dubai · Abu Dhabi · Sharjah · Ajman",
      languageToggle: "العربية",
    },

    // Hero
    hero: {
      eyebrow: "Integrated Facility Management & Service Solutions Provider",
      title: "Reduce Downtime. Cut Costs. One Facility Management Partner.",
      subtitle: "Cleaning, Maintenance & Manpower solutions under SLA-driven contracts across Dubai & the UAE.",
      disclosure: "Services are delivered through in-house teams and certified specialist partners where required.",
      ctaPrimary: "Get Free Facility Audit",
      ctaSecondary: "Chat on WhatsApp",
      stat1: "Avg Response Time",
      stat2: "Active SLAs",
      stat3: "Trained Specialists",
      stat4: "Uptime SLA",
    },

    // Free Audit banner
    audit: {
      eyebrow: "Free Offer · No Obligation",
      title: "FREE Facility Health Check",
      desc: "A senior consultant inspects your site and emails you a 60-point report within 48 hours.",
      inspectLabel: "What we inspect",
      items: ["HVAC systems & chillers", "Preventive maintenance gaps", "Cleaning & hygiene standards", "Fire, electrical & compliance", "Vendor cost benchmarking"],
      cta: "Book Free Assessment",
    },

    // Trust / How we deliver
    trust: {
      sectionLabel: "How we deliver",
      sectionTitle: "Built on the four delivery pillars UAE operators demand.",
      items: [
        { title: "24/7 Support Availability", desc: "Round-the-clock hotline, emergency dispatch and a live control room for all AMC clients." },
        { title: "Fast Response Times", desc: "SLA-bound: 30 min critical, 2 hr major, 8 hr minor. Measured, reported, guaranteed." },
        { title: "Reliable Manpower Supply", desc: "Sponsored, trained and verified teams. Surge from 5 to 500 staff in 72 hours." },
        { title: "Consistent Service Delivery", desc: "Standardised SOPs, uniformed staff and monthly KPI reporting across every site." },
      ],
    },

    // Services
    services: {
      sectionLabel: "What We Deliver",
      sectionTitle: "One partner. Every facility need.",
      sectionDesc: "Hard FM, soft FM, manpower and AMC under a single SLA. Less vendor admin, predictable OPEX, full accountability.",
      fm: {
        title: "Facility Management",
        slug: "facility-management",
        short: "Hard & Soft FM under one SLA",
        long: "End-to-end integrated facility management combining hard FM (HVAC, MEP, plumbing, electrical, fire safety) with soft FM (cleaning, pest control, landscaping, waste). One contract, one accountable partner.",
      },
      cleaning: {
        title: "Cleaning Services",
        slug: "cleaning",
        short: "Commercial, residential and deep cleaning",
        long: "Daily, weekly and one-off cleaning programs for offices, retail, warehouses, residential buildings and post-construction handover.",
      },
      maintenance: {
        title: "Maintenance & Installation",
        slug: "maintenance",
        short: "AC, MEP, electrical and handyman services",
        long: "Preventive and reactive maintenance for HVAC, plumbing, electrical, carpentry, painting, and full installation works backed by certified technicians.",
      },
      manpower: {
        title: "Staff Supply / Manpower",
        slug: "manpower",
        short: "Trained, verified and scalable teams",
        long: "On-demand and long-term manpower supply: cleaners, technicians, helpers, drivers and supervisors. Fully sponsored, trained and HSE compliant.",
      },
      snagging: {
        title: "Property Snagging",
        slug: "snagging-services-uae",
        short: "Independent handover inspection reports",
        long: "Detailed pre-handover snagging inspection for new villas, apartments and offices with 48-hour photo reports and developer follow-up.",
      },
      projects: {
        title: "Project Management",
        slug: "project-management-uae",
        short: "End-to-end fit-out & FM project delivery",
        long: "Turn-key project management for fit-outs, refurbishments and building-services upgrades across the UAE — scope, budget, schedule and safety.",
      },
    },

    // Why Choose Us
    why: {
      sectionLabel: "Why Orion",
      sectionTitle: "Built for operators who can't afford downtime.",
      items: [
        { title: "Single Vendor Convenience", desc: "Replace 5+ contractors with one accountable partner. One number to call. One report to read." },
        { title: "SLA-Driven Contracts", desc: "Response times, uptime, and quality KPIs written into every contract — and reported on monthly." },
        { title: "Preventive, Not Reactive", desc: "Scheduled PPM catches issues before they become emergencies. Less downtime, lower OPEX." },
        { title: "Trained & Verified Staff", desc: "All team members are sponsored, background-checked, HSE-trained and uniformed." },
        { title: "Scalable Manpower", desc: "Surge from 5 to 500 staff in 72 hours. Perfect for events, peak season and project launches." },
        { title: "Transparent Reporting", desc: "Live ticketing, monthly performance reports and a dedicated account manager. Zero black boxes." },
      ],
    },

    // Industries
    industries: {
      sectionLabel: "Sectors We Serve",
      sectionTitle: "Trusted across the UAE economy.",
      items: [
        { slug: "logistics", title: "Logistics & Warehousing", desc: "DIP, JAFZA, KIZAD, Khalifa Port. High-bay cleaning, dust control, dock maintenance." },
        { slug: "residential", title: "Residential Towers & Communities", desc: "Towers, communities and villa clusters. Common-area cleaning, MEP, lift, pool and landscape." },
        { slug: "commercial", title: "Commercial Offices", desc: "Grade A offices, co-working and corporate HQ. Day porter, night cleaning, AMC." },
        { slug: "retail", title: "Retail & F&B", desc: "Malls, restaurants and showrooms. Front-of-house standards backed by HACCP discipline." },
        { slug: "healthcare", title: "Healthcare", desc: "Clinics, pharmacies and labs. Medical-grade cleaning protocols and infection control." },
        { slug: "education", title: "Education", desc: "Schools and training centers. Child-safe products, term-break deep cleans, holiday PPM." },
      ],
    },

    // About
    about: {
      sectionLabel: "About Orion FM",
      title: "Our hands maintain it. Our heart protects it.",
      story: "Orion Facilities Management was founded to solve a simple problem facing UAE operators: too many vendors, too much admin, not enough accountability. We unify hard FM, soft FM, cleaning and manpower under one SLA-driven contract — so logistics, commercial and residential leaders can focus on their core business while we keep their facility running.",
      missionLabel: "Our Mission",
      mission: "To be the UAE's most trusted integrated FM partner — guaranteeing uptime, efficiency and compliance through a single vendor solution.",
      visionLabel: "Our Vision",
      vision: "A region where every operator runs a clean, safe and high-performing facility without the chaos of multi-vendor coordination.",
      valuesLabel: "Our Values — SPIA",
      values: [
        { title: "Safety", desc: "HSE first, always. Zero compromise on staff and client safety." },
        { title: "Professionalism", desc: "Uniformed, trained and respectful teams in every facility." },
        { title: "Integrity", desc: "Transparent reporting, honest pricing, no hidden surprises." },
        { title: "Attention to Detail", desc: "The 1% details others miss are the 99% of why clients stay." },
      ],
    },

    // AMC
    amc: {
      sectionLabel: "AMC & SLA Contracts",
      title: "Predictable OPEX. Guaranteed uptime.",
      desc: "Our Annual Maintenance Contracts wrap preventive maintenance, emergency response, monthly reporting and a dedicated account manager into one fixed monthly fee.",
      includes: "What an Orion AMC includes",
      items: [
        "Scheduled preventive maintenance (PPM) calendar",
        "24/7 emergency response with SLA-bound times",
        "Dedicated account manager and single point of contact",
        "Monthly performance reporting and quarterly business reviews",
        "Spare parts management and asset register",
        "HSE compliance and full insurance coverage",
      ],
      cta: "Request AMC Proposal",
    },

    // Contact
    contact: {
      sectionLabel: "Get in touch",
      title: "Tell us about your facility.",
      desc: "Reply within 1 business hour. Free site inspection for qualifying facilities.",
      formName: "Full Name",
      formEmail: "Email",
      formPhone: "Phone (UAE)",
      formService: "Service of Interest",
      formMessage: "How can we help?",
      submit: "Send Request",
      addressLabel: "Office",
      address: "Ajman Free Zone, Sheikh Rashid Bin Saeed Al Maktoum St, Al Bustan, Ajman, UAE",
      getDirections: "Get Directions",
      hoursLabel: "Hours",
      hours: "Sun – Thu · 8:00 AM – 6:00 PM",
      phoneLabel: "Phone / WhatsApp",
    },

    // Blog
    blog: {
      sectionLabel: "Insights",
      title: "Resources for UAE facility leaders.",
      readMore: "Read article",
      readingTime: "min read",
      relatedTitle: "Continue reading",
    },

    // CTA Banner
    ctaBanner: {
      eyebrow: "Free site inspection",
      title: "See your facility cost savings — in 30 minutes.",
      desc: "Book a free walk-through with a senior FM consultant. No obligation, just a clear cost-saving plan.",
      cta: "Book Free Inspection",
    },

    // Lead form
    leadForm: {
      heading: "Get a Free Quote",
      sub: "Tell us your facility type and challenge. We'll respond within 1 business hour.",
    },

    // Cookie banner
    cookie: {
      message: "We use cookies to improve your experience and comply with UAE PDPL. By using this site, you accept our cookie usage.",
      accept: "Accept",
      decline: "Decline",
    },

    // Footer
    footer: {
      tagline: "Integrated FM partner trusted across the UAE.",
      quickLinks: "Quick Links",
      ourServices: "Our Services",
      contact: "Contact",
      legal: "Legal",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      copyright: "© 2026 Orion Facilities Management. All rights reserved.",
      built: "Serving Dubai, Abu Dhabi, Sharjah & Ajman",
    },

    // Testimonials
    testimonials: {
      sectionLabel: "What clients say",
      sectionTitle: "Operators don't switch back to multi-vendor.",
      items: [
        { quote: "Switching to Orion cut our facility OPEX 22% in the first year. Their PPM discipline is unmatched in the UAE.", author: "Operations Director", company: "3PL Logistics, Jebel Ali" },
        { quote: "One contract instead of seven. One report instead of seven. The simplification alone was worth the move.", author: "Facility Manager", company: "Commercial Tower, Business Bay" },
        { quote: "Their manpower team scaled us from 12 to 45 staff in a week for our peak season. Zero quality drop.", author: "Country Manager", company: "Retail Group, Dubai Mall" },
      ],
    },

    // Privacy
    privacy: {
      title: "Privacy Policy",
      lastUpdated: "Last updated: February 2026",
      intro: "Orion Facilities Management respects your privacy and is committed to compliance with the UAE Personal Data Protection Law (PDPL). This policy explains how we collect, use and protect your information.",
      sections: [
        { heading: "Information we collect", body: "Name, email, phone number, company and any details you provide via our contact and quote forms. We also collect basic analytics and cookie data for site performance." },
        { heading: "How we use it", body: "To respond to your inquiry, send proposals, provide service updates and improve our website. We do not sell your data." },
        { heading: "Your rights under UAE PDPL", body: "You can request access, correction or deletion of your personal data at any time by emailing us." },
        { heading: "Cookies", body: "We use essential and analytics cookies. You can decline non-essential cookies via the banner shown on first visit." },
        { heading: "Contact", body: "Questions about this policy? Email privacy@orion-fm.ae." },
      ],
    },

    // Tools (lead magnets)
    tools: {
      sectionLabel: "Free Tools",
      sectionTitle: "Two free tools to help you decide.",
      sectionDesc: "Use our calculator and health check to benchmark your facility — no commitment, no sales call required.",
      calc: {
        title: "UAE Maintenance Cost Calculator",
        slug: "cost-calculator",
        short: "Estimate your annual FM cost in 60 seconds.",
        cta: "Calculate now",
        step: "Step",
        of: "of",
        next: "Next",
        back: "Back",
        finish: "Get my estimate",
        s1Title: "What type of facility?",
        s2Title: "How large is the facility?",
        s2Hint: "Drag or type the area in square feet.",
        sqftLabel: "Square feet",
        s3Title: "Which services do you need?",
        s3Hint: "Pick all that apply.",
        s4Title: "How many vendors do you currently use?",
        s5Title: "Where should we send your estimate?",
        resultEyebrow: "Your estimate",
        resultTitle: "Here's your potential annual saving",
        currentLabel: "Current annual cost",
        orionLabel: "With Orion (single vendor)",
        savingsLabel: "Annual savings",
        savingsPctLabel: "savings vs multi-vendor",
        bandLabel: "Likely range:",
        disclaimer: "Estimate based on 2026 UAE FM market averages — actual quote is set after a free site inspection. Pricing shown is an indicative range, not a binding offer.",
        bookCta: "Book free site inspection",
        ftypes: [
          { id: "warehouse", label: "Warehouse / Logistics" },
          { id: "office", label: "Commercial Office" },
          { id: "residential", label: "Residential Building" },
          { id: "retail", label: "Retail / F&B" },
          { id: "healthcare", label: "Healthcare" },
        ],
        servicesList: [
          { id: "cleaning", label: "Cleaning & Soft FM" },
          { id: "hvac", label: "HVAC & MEP" },
          { id: "fire", label: "Fire & Life Safety" },
          { id: "manpower", label: "Manpower / Helpers" },
          { id: "pest", label: "Pest & Landscaping" },
        ],
        vendorOptions: [
          { value: 1, label: "Just 1" },
          { value: 2, label: "2 vendors" },
          { value: 3, label: "3 vendors" },
          { value: 4, label: "4+ vendors" },
        ],
      },
      health: {
        title: "Free Facility Health Check",
        slug: "health-check",
        short: "60-point UAE-ready facility audit checklist (PDF).",
        formHeading: "Get your free PDF",
        formSub: "We'll email it instantly and a senior consultant will follow up within 1 business hour.",
        downloadBtn: "Download Health Check (PDF)",
        successTitle: "Your PDF is ready",
        successMsg: "Click the button below to download. We've also emailed it to you.",
        bullets: [
          "60+ inspection points across HVAC, electrical, plumbing, fire, cleaning",
          "UAE municipality-aligned compliance items",
          "Cost-saving opportunity score (1-100)",
          "Use it as a vendor scorecard or for in-house audits",
        ],
      },
    },

    // Service detail benefits & FAQs
    serviceDetail: {
      benefitsLabel: "Key Benefits",
      useCasesLabel: "Use Cases",
      faqLabel: "Frequently Asked Questions",
      ctaTitle: "Ready to start?",
      ctaDesc: "Book a free site inspection with a senior consultant.",
    },

    // Terms of Service
    terms: {
      title: "Terms of Service",
      lastUpdated: "Last updated: February 2026",
      intro: "These Terms govern your use of the Orion Facilities Management website and services. By engaging Orion, you agree to these Terms.",
      sections: [
        { heading: "Services", body: "Orion Facilities Management provides integrated facility management and service solutions, including cleaning, maintenance, AMC and manpower supply. Specialist or regulated engineering works may be delivered through certified partner contractors under our single-vendor SLA." },
        { heading: "Quotes & Contracts", body: "All quotes are valid for 30 days unless stated otherwise. Final scope, pricing and SLAs are governed by the signed service agreement or AMC." },
        { heading: "Payment", body: "Invoices are issued per the contract cycle (monthly/quarterly/annual). Payment terms are 30 days net unless agreed otherwise. Late payments may attract interest and service suspension." },
        { heading: "Liability", body: "Orion's liability is limited to the value of the service fee paid in the preceding 3 months. We carry general liability and workmen's compensation insurance. We are not liable for indirect, consequential or loss-of-business damages." },
        { heading: "Client Obligations", body: "Clients shall provide safe access, accurate briefs, and necessary approvals. Clients remain responsible for obtaining any government permits required for works on their premises." },
        { heading: "Confidentiality", body: "Both parties agree to keep commercial information confidential for the duration of the engagement and 2 years after termination." },
        { heading: "Governing Law", body: "These Terms are governed by the laws of the United Arab Emirates. Disputes shall be resolved in the courts of Ajman or Dubai, UAE." },
        { heading: "Contact", body: "Questions? Email legal@OrionFM.ae or call +971 50 680 7552." },
      ],
    },

    // Logistics landing page
    logistics: {
      metaTitle: "Facility Management for Warehouses & Logistics in UAE",
      metaDesc: "Integrated FM for warehouses and 3PL operators in UAE — zero downtime, preventive maintenance, peak-season manpower, SLA-backed contracts.",
      eyebrow: "Primary Focus · Warehousing & Logistics",
      title: "Facility Management for Warehouses & Logistics in the UAE",
      subtitle: "Zero downtime. Preventive maintenance. Peak-season manpower. SLA-backed contracts for DP World, JAFZA, DIP, KIZAD and Khalifa Port tenants.",
      ctaPrimary: "Get Free Facility Audit",
      ctaSecondary: "Chat on WhatsApp",
      pillarsLabel: "Built for logistics operators",
      pillars: [
        { title: "Zero Downtime", desc: "Chillers, MHE power, dock levellers and fire systems stay online through peak seasons with predictive PPM." },
        { title: "Preventive Maintenance", desc: "Scheduled HVAC, electrical, fire and plumbing PPM aligned with your pick-pack-ship SLAs." },
        { title: "Peak Manpower Supply", desc: "Surge cleaners, helpers and forklift operators in 72 hours for DSF, Ramadan and Q4 peak." },
        { title: "SLA-Based Contracts", desc: "Response times, uptime, quality KPIs and monthly reports written into every contract." },
      ],
      scopeLabel: "Scope of work",
      scope: [
        "High-bay racking vacuuming and dust control",
        "Forklift-aisle scrubber-drying and line-marking refresh",
        "Dock-door greasing, leveller PPM and shutter maintenance",
        "Outdoor yard sweeping and drain jetting",
        "Fire pump weekly testing, sprinkler and detection PPM",
        "Toilets, mess-room and office daily housekeeping",
        "Pest control, waste management and landscape",
        "Security manpower and access-control AMC",
      ],
      benchmarksLabel: "Benchmarks we hit",
      benchmarks: [
        { v: "<1hr", l: "Critical response" },
        { v: "99.6%", l: "Asset uptime SLA" },
        { v: "22%", l: "Avg OPEX reduction" },
        { v: "72hr", l: "Manpower surge" },
      ],
      ctaTitle: "Audit your warehouse free.",
      ctaDesc: "A senior consultant walks your facility, benchmarks your current FM spend and emails a 60-point report within 48 hours.",
    },
  },

  ar: {
    nav: {
      home: "الرئيسية",
      about: "من نحن",
      services: "خدماتنا",
      industries: "القطاعات",
      amc: "عقود الصيانة",
      blog: "المدونة",
      careers: "الوظائف",
      contact: "اتصل بنا",
      getQuote: "اطلب عرض سعر",
    },
    common: {
      whatsapp: "واتساب الآن",
      callNow: "اتصل الآن",
      learnMore: "اعرف المزيد",
      readMore: "اقرأ المزيد",
      viewAll: "عرض الكل",
      requestQuote: "اطلب عرض سعر",
      bookConsultation: "احجز استشارة",
      submitting: "جارٍ الإرسال…",
      successTitle: "تم استلام طلبك",
      successMsg: "سيتواصل فريقنا معك خلال ساعة عمل.",
      errorMsg: "حدث خطأ. حاول مرة أخرى أو راسلنا عبر واتساب.",
      requiredField: "حقل مطلوب",
      selectService: "اختر الخدمة",
      cities: "دبي · أبوظبي · الشارقة · عجمان",
      languageToggle: "English",
    },
    hero: {
      eyebrow: "مزود حلول متكاملة لإدارة المرافق والخدمات",
      title: "قلّل التوقف. خفّض التكاليف. شريك واحد لإدارة المرافق.",
      subtitle: "حلول التنظيف والصيانة والقوى العاملة ضمن عقود ملتزمة باتفاقيات مستوى الخدمة في دبي والإمارات.",
      disclosure: "تُقدَّم الخدمات من خلال فرقنا الداخلية وشركائنا المتخصصين المعتمدين عند الحاجة.",
      ctaPrimary: "احصل على تدقيق مجاني",
      ctaSecondary: "محادثة على واتساب",
      stat1: "وقت الاستجابة",
      stat2: "اتفاقيات نشطة",
      stat3: "متخصص مدرب",
      stat4: "وقت تشغيل مضمون",
    },
    audit: {
      eyebrow: "عرض مجاني · بدون التزام",
      title: "فحص صحي مجاني لمنشأتك",
      desc: "يفحص استشاري كبير موقعك ويرسل إليك تقريرًا من 60 نقطة خلال 48 ساعة.",
      inspectLabel: "ما نفحصه",
      items: ["أنظمة التكييف والمبردات", "فجوات الصيانة الوقائية", "معايير التنظيف والنظافة", "الحرائق والكهرباء والامتثال", "معايرة تكاليف الموردين"],
      cta: "احجز تقييمًا مجانيًا",
    },
    trust: {
      sectionLabel: "كيف نقدّم الخدمة",
      sectionTitle: "مبني على الركائز الأربع التي يطلبها مشغلو الإمارات.",
      items: [
        { title: "دعم على مدار الساعة", desc: "خط ساخن على مدار الساعة وإرسال طوارئ وغرفة تحكم مباشرة لجميع عملاء عقد الصيانة السنوي." },
        { title: "أوقات استجابة سريعة", desc: "ملتزم باتفاقية مستوى الخدمة: 30 دقيقة للحرج، ساعتان للرئيسي، 8 ساعات للثانوي. مُقاس، مُوثَّق، مضمون." },
        { title: "توريد قوى عاملة موثوق", desc: "فرق مكفولة ومدربة وموثقة. توسيع من 5 إلى 500 موظف في 72 ساعة." },
        { title: "تقديم خدمة متسق", desc: "إجراءات معيارية موحدة، موظفون بزي موحد، وتقارير مؤشرات أداء شهرية في كل موقع." },
      ],
    },
    services: {
      sectionLabel: "ما نقدمه",
      sectionTitle: "شريك واحد. لكل احتياجات مرافقك.",
      sectionDesc: "إدارة المرافق الصلبة واللينة والقوى العاملة وعقود الصيانة السنوية تحت اتفاقية واحدة. أقل إدارة موردين، نفقات تشغيلية متوقعة، ومساءلة كاملة.",
      fm: {
        title: "إدارة المرافق",
        slug: "facility-management",
        short: "إدارة مرافق صلبة ولينة في عقد واحد",
        long: "إدارة مرافق متكاملة شاملة تجمع بين الصيانة الصلبة (التكييف والكهروميكانيك والسباكة والكهرباء والسلامة من الحرائق) والصيانة اللينة (التنظيف ومكافحة الآفات والمناظر الطبيعية والنفايات).",
      },
      cleaning: {
        title: "خدمات التنظيف",
        slug: "cleaning",
        short: "تنظيف تجاري وسكني وعميق",
        long: "برامج تنظيف يومية وأسبوعية ومرة واحدة للمكاتب والتجزئة والمستودعات والمباني السكنية وما بعد البناء.",
      },
      maintenance: {
        title: "الصيانة والتركيب",
        slug: "maintenance",
        short: "تكييف وكهروميكانيك وكهرباء وأعمال يدوية",
        long: "صيانة وقائية وتفاعلية لأنظمة التكييف والسباكة والكهرباء والنجارة والدهان وأعمال التركيب الكاملة بدعم من فنيين معتمدين.",
      },
      manpower: {
        title: "توريد القوى العاملة",
        slug: "manpower",
        short: "فرق مدربة وموثقة وقابلة للتوسع",
        long: "توريد قوى عاملة عند الطلب وطويل الأجل: عمال نظافة، فنيون، مساعدون، سائقون ومشرفون. مكفولون بالكامل ومدربون وملتزمون بالصحة والسلامة والبيئة.",
      },
      snagging: {
        title: "فحص العقار (Snagging)",
        slug: "snagging-services-uae",
        short: "تقارير فحص تسليم مستقلة",
        long: "فحص تسليم مفصل قبل الاستلام للفلل والشقق والمكاتب الجديدة مع تقارير مصورة خلال 48 ساعة ومتابعة مع المطور.",
      },
      projects: {
        title: "إدارة المشاريع",
        slug: "project-management-uae",
        short: "تسليم مشاريع FM والتشطيب من البداية للنهاية",
        long: "إدارة مشاريع شاملة للتشطيبات والتجديدات وترقيات الخدمات المبنى في الإمارات — النطاق والميزانية والجدول والسلامة.",
      },
    },
    why: {
      sectionLabel: "لماذا أوريون",
      sectionTitle: "مصمم للمشغلين الذين لا يستطيعون تحمل التوقف.",
      items: [
        { title: "راحة المورد الواحد", desc: "استبدل 5 مقاولين أو أكثر بشريك واحد مسؤول. رقم واحد للاتصال. تقرير واحد للقراءة." },
        { title: "عقود ملتزمة باتفاقيات مستوى الخدمة", desc: "أوقات الاستجابة ووقت التشغيل ومؤشرات الجودة الرئيسية مكتوبة في كل عقد - ويتم الإبلاغ عنها شهريًا." },
        { title: "وقائي، وليس تفاعلي", desc: "الصيانة الوقائية المجدولة تكتشف المشكلات قبل أن تصبح طوارئ. أقل توقفًا، نفقات تشغيلية أقل." },
        { title: "موظفون مدربون وموثقون", desc: "جميع أعضاء الفريق مكفولون ومدققون خلفياتهم ومدربون على الصحة والسلامة والبيئة وبزي موحد." },
        { title: "قوى عاملة قابلة للتوسع", desc: "زد من 5 إلى 500 موظف في 72 ساعة. مثالي للفعاليات وموسم الذروة وإطلاق المشاريع." },
        { title: "تقارير شفافة", desc: "تذاكر مباشرة وتقارير أداء شهرية ومدير حساب مخصص. صفر صناديق سوداء." },
      ],
    },
    industries: {
      sectionLabel: "القطاعات التي نخدمها",
      sectionTitle: "موثوق به في جميع أنحاء اقتصاد الإمارات.",
      items: [
        { slug: "logistics", title: "اللوجستيات والتخزين", desc: "مجمع دبي للاستثمار، جافزا، كيزاد، ميناء خليفة. تنظيف الرفوف العالية، التحكم في الغبار، صيانة الأرصفة." },
        { slug: "residential", title: "الأبراج السكنية والمجتمعات", desc: "أبراج ومجتمعات ومجموعات فلل. تنظيف المناطق المشتركة، الكهروميكانيك، المصاعد، المسابح والمناظر الطبيعية." },
        { slug: "commercial", title: "المكاتب التجارية", desc: "مكاتب الفئة A، مساحات العمل المشتركة، المقرات الرئيسية. خدمة نهارية، تنظيف ليلي، عقد صيانة سنوي." },
        { slug: "retail", title: "التجزئة والمطاعم", desc: "مولات ومطاعم وصالات عرض. معايير الواجهة الأمامية مدعومة بانضباط HACCP." },
        { slug: "healthcare", title: "الرعاية الصحية", desc: "عيادات وصيدليات ومختبرات. بروتوكولات تنظيف بدرجة طبية ومكافحة العدوى." },
        { slug: "education", title: "التعليم", desc: "مدارس ومراكز تدريب. منتجات آمنة للأطفال، تنظيف عميق بين الفصول، صيانة العطلات." },
      ],
    },
    about: {
      sectionLabel: "عن أوريون",
      title: "أيدينا تصونه. قلوبنا تحميه.",
      story: "تأسست أوريون لإدارة المرافق لحل مشكلة بسيطة يواجهها المشغلون في الإمارات: عدد كبير من الموردين، إدارة مفرطة، وعدم كافٍ من المساءلة. نوحد إدارة المرافق الصلبة واللينة والتنظيف والقوى العاملة تحت عقد واحد ملتزم باتفاقية مستوى الخدمة.",
      missionLabel: "مهمتنا",
      mission: "أن نكون الشريك الأكثر ثقة لإدارة المرافق المتكاملة في الإمارات - نضمن وقت التشغيل والكفاءة والامتثال من خلال حل مورد واحد.",
      visionLabel: "رؤيتنا",
      vision: "منطقة يدير فيها كل مشغل مرفقًا نظيفًا وآمنًا وعالي الأداء دون فوضى تنسيق متعدد الموردين.",
      valuesLabel: "قيمنا — SPIA",
      values: [
        { title: "السلامة", desc: "الصحة والسلامة والبيئة أولاً، دائمًا. صفر تنازلات بشأن سلامة الموظفين والعملاء." },
        { title: "الاحترافية", desc: "فرق بزي موحد ومدربة ومحترمة في كل منشأة." },
        { title: "النزاهة", desc: "تقارير شفافة، تسعير صادق، لا مفاجآت خفية." },
        { title: "الاهتمام بالتفاصيل", desc: "التفاصيل بنسبة 1٪ التي يفوتها الآخرون هي 99٪ من سبب بقاء العملاء." },
      ],
    },
    amc: {
      sectionLabel: "عقود الصيانة السنوية",
      title: "نفقات تشغيلية متوقعة. وقت تشغيل مضمون.",
      desc: "تجمع عقود الصيانة السنوية لدينا بين الصيانة الوقائية والاستجابة للطوارئ والتقارير الشهرية ومدير حساب مخصص في رسوم شهرية ثابتة.",
      includes: "ما يشمله عقد AMC مع أوريون",
      items: [
        "تقويم صيانة وقائية مجدولة",
        "استجابة طوارئ على مدار الساعة بأوقات ملتزمة باتفاقية مستوى الخدمة",
        "مدير حساب مخصص ونقطة اتصال واحدة",
        "تقارير أداء شهرية ومراجعات أعمال ربع سنوية",
        "إدارة قطع الغيار وسجل الأصول",
        "الامتثال للصحة والسلامة والبيئة وتغطية تأمينية كاملة",
      ],
      cta: "اطلب عرض AMC",
    },
    contact: {
      sectionLabel: "تواصل معنا",
      title: "أخبرنا عن منشأتك.",
      desc: "نرد خلال ساعة عمل واحدة. تفتيش موقع مجاني للمرافق المؤهلة.",
      formName: "الاسم الكامل",
      formEmail: "البريد الإلكتروني",
      formPhone: "الهاتف (الإمارات)",
      formService: "الخدمة المطلوبة",
      formMessage: "كيف يمكننا المساعدة؟",
      submit: "إرسال الطلب",
      addressLabel: "المكتب",
      address: "المنطقة الحرة بعجمان، شارع الشيخ راشد بن سعيد آل مكتوم، البستان، عجمان، الإمارات",
      getDirections: "احصل على الاتجاهات",
      hoursLabel: "ساعات العمل",
      hours: "الأحد - الخميس · 8:00 صباحًا - 6:00 مساءً",
      phoneLabel: "الهاتف / واتساب",
    },
    blog: {
      sectionLabel: "رؤى",
      title: "موارد لقادة المرافق في الإمارات.",
      readMore: "اقرأ المقال",
      readingTime: "دقيقة قراءة",
      relatedTitle: "تابع القراءة",
    },
    ctaBanner: {
      eyebrow: "تفتيش موقع مجاني",
      title: "شاهد توفير تكاليف منشأتك — في 30 دقيقة.",
      desc: "احجز جولة مجانية مع استشاري إدارة مرافق كبير. بدون التزام، فقط خطة واضحة لتوفير التكاليف.",
      cta: "احجز التفتيش المجاني",
    },
    leadForm: {
      heading: "احصل على عرض سعر مجاني",
      sub: "أخبرنا بنوع منشأتك وتحدياتك. سنرد خلال ساعة عمل واحدة.",
    },
    cookie: {
      message: "نستخدم ملفات تعريف الارتباط لتحسين تجربتك والامتثال لقانون حماية البيانات الشخصية الإماراتي. باستخدام هذا الموقع، فإنك تقبل استخدامنا لملفات تعريف الارتباط.",
      accept: "أقبل",
      decline: "أرفض",
    },
    footer: {
      tagline: "شريك إدارة مرافق متكامل موثوق به في جميع أنحاء الإمارات.",
      quickLinks: "روابط سريعة",
      ourServices: "خدماتنا",
      contact: "اتصل بنا",
      legal: "قانوني",
      privacy: "سياسة الخصوصية",
      terms: "شروط الخدمة",
      copyright: "© 2026 أوريون لإدارة المرافق. جميع الحقوق محفوظة.",
      built: "نخدم دبي وأبوظبي والشارقة وعجمان",
    },
    testimonials: {
      sectionLabel: "ماذا يقول العملاء",
      sectionTitle: "المشغلون لا يعودون إلى الموردين المتعددين.",
      items: [
        { quote: "التحول إلى أوريون قلل من نفقاتنا التشغيلية بنسبة 22٪ في السنة الأولى. انضباط الصيانة الوقائية لديهم لا مثيل له في الإمارات.", author: "مدير العمليات", company: "شركة لوجستيات، جبل علي" },
        { quote: "عقد واحد بدلاً من سبعة. تقرير واحد بدلاً من سبعة. التبسيط وحده كان يستحق الانتقال.", author: "مدير المرافق", company: "برج تجاري، الخليج التجاري" },
        { quote: "وسع فريقهم لدينا من 12 إلى 45 موظفًا في أسبوع لموسم الذروة لدينا. صفر انخفاض في الجودة.", author: "مدير الدولة", company: "مجموعة تجزئة، دبي مول" },
      ],
    },
    privacy: {
      title: "سياسة الخصوصية",
      lastUpdated: "آخر تحديث: فبراير 2026",
      intro: "تحترم أوريون لإدارة المرافق خصوصيتك وملتزمة بالامتثال لقانون حماية البيانات الشخصية الإماراتي. تشرح هذه السياسة كيف نجمع معلوماتك ونستخدمها ونحميها.",
      sections: [
        { heading: "المعلومات التي نجمعها", body: "الاسم، البريد الإلكتروني، رقم الهاتف، الشركة وأي تفاصيل تقدمها عبر نماذج الاتصال وعروض الأسعار." },
        { heading: "كيف نستخدمها", body: "للرد على استفسارك، إرسال المقترحات، تقديم تحديثات الخدمة وتحسين موقعنا. نحن لا نبيع بياناتك." },
        { heading: "حقوقك بموجب PDPL الإماراتي", body: "يمكنك طلب الوصول إلى بياناتك الشخصية أو تصحيحها أو حذفها في أي وقت عن طريق مراسلتنا عبر البريد الإلكتروني." },
        { heading: "ملفات تعريف الارتباط", body: "نستخدم ملفات تعريف ارتباط أساسية وتحليلية. يمكنك رفض ملفات تعريف الارتباط غير الأساسية عبر الشعار المعروض في الزيارة الأولى." },
        { heading: "الاتصال", body: "أسئلة حول هذه السياسة؟ راسلنا على privacy@orion-fm.ae." },
      ],
    },
    serviceDetail: {
      benefitsLabel: "الفوائد الرئيسية",
      useCasesLabel: "حالات الاستخدام",
      faqLabel: "الأسئلة الشائعة",
      ctaTitle: "هل أنت مستعد للبدء؟",
      ctaDesc: "احجز تفتيش موقع مجاني مع استشاري كبير.",
    },
    tools: {
      sectionLabel: "أدوات مجانية",
      sectionTitle: "أداتان مجانيتان لمساعدتك على اتخاذ القرار.",
      sectionDesc: "استخدم الحاسبة والفحص الصحي لقياس منشأتك - دون التزام، دون مكالمة مبيعات.",
      calc: {
        title: "حاسبة تكاليف الصيانة في الإمارات",
        slug: "cost-calculator",
        short: "قدّر تكلفة إدارة المرافق السنوية في 60 ثانية.",
        cta: "احسب الآن",
        step: "الخطوة",
        of: "من",
        next: "التالي",
        back: "السابق",
        finish: "احصل على التقدير",
        s1Title: "ما نوع منشأتك؟",
        s2Title: "ما حجم المنشأة؟",
        s2Hint: "اسحب أو اكتب المساحة بالقدم المربع.",
        sqftLabel: "قدم مربع",
        s3Title: "ما الخدمات التي تحتاجها؟",
        s3Hint: "اختر كل ما ينطبق.",
        s4Title: "كم عدد الموردين الذين تستخدمهم حاليًا؟",
        s5Title: "أين نرسل التقدير؟",
        resultEyebrow: "تقديرك",
        resultTitle: "إليك التوفير السنوي المحتمل",
        currentLabel: "التكلفة السنوية الحالية",
        orionLabel: "مع أوريون (مورد واحد)",
        savingsLabel: "التوفير السنوي",
        savingsPctLabel: "توفير مقابل تعدد الموردين",
        bandLabel: "النطاق المتوقع:",
        disclaimer: "تقدير بناءً على متوسطات سوق إدارة المرافق في الإمارات لعام 2026 — يُحدَّد العرض الفعلي بعد تفتيش موقع مجاني. السعر المعروض نطاق إرشادي وليس عرضاً ملزماً.",
        bookCta: "احجز تفتيش موقع مجاني",
        ftypes: [
          { id: "warehouse", label: "مستودع / لوجستيات" },
          { id: "office", label: "مكتب تجاري" },
          { id: "residential", label: "مبنى سكني" },
          { id: "retail", label: "تجزئة / مطاعم" },
          { id: "healthcare", label: "رعاية صحية" },
        ],
        servicesList: [
          { id: "cleaning", label: "تنظيف وصيانة لينة" },
          { id: "hvac", label: "تكييف وكهروميكانيك" },
          { id: "fire", label: "السلامة من الحرائق" },
          { id: "manpower", label: "قوى عاملة / مساعدون" },
          { id: "pest", label: "مكافحة آفات ومناظر طبيعية" },
        ],
        vendorOptions: [
          { value: 1, label: "مورد واحد" },
          { value: 2, label: "موردان" },
          { value: 3, label: "3 موردين" },
          { value: 4, label: "4 موردين أو أكثر" },
        ],
      },
      health: {
        title: "فحص صحي مجاني للمنشأة",
        slug: "health-check",
        short: "قائمة تدقيق منشأة من 60 نقطة جاهزة للإمارات (PDF).",
        formHeading: "احصل على ملف PDF مجاني",
        formSub: "سنرسله لك فورًا وسيتابع معك استشاري كبير خلال ساعة عمل.",
        downloadBtn: "تحميل الفحص الصحي (PDF)",
        successTitle: "ملف PDF جاهز",
        successMsg: "انقر على الزر أدناه للتحميل. لقد أرسلناه أيضًا إلى بريدك الإلكتروني.",
        bullets: [
          "60+ نقطة تفتيش عبر التكييف والكهرباء والسباكة والحرائق والتنظيف",
          "بنود امتثال متوافقة مع بلديات الإمارات",
          "نقاط فرص توفير التكاليف (1-100)",
          "استخدمها كبطاقة تقييم للموردين أو للتدقيقات الداخلية",
        ],
      },
    },
  },
};

export const COMPANY = {
  whatsapp: "971585524229",
  whatsappDisplay: "+971 58 552 4229",
  phone: "+971585524229",
  phoneDisplay: "+971 58 552 4229",
  email: "info@orionfm.ae",
  emailPrivacy: "privacy@orionfm.ae",
  emailCareers: "careers@orionfm.ae",
  domain: "www.orionfm.ae",
  brand: "Orion FM",
  brandFull: "Orion Facilities Management",
  tagline: "Empowering progress through precision and partnership.",
  taglineAr: "تمكين التقدم من خلال الدقة والشراكة.",
  logo: "https://customer-assets.emergentagent.com/job_orion-integrated-fm/artifacts/mnerl92k_Orion%20Logo.png",
  banner: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/x9bly66f_WhatsApp%20Image%202026-09-02%20at%2010.52.05%20PM.jpeg",
  social: {
    instagram: "https://instagram.com/orionfacilitiesmgt",
    instagramHandle: "@orionfacilitiesmgt",
    facebook: "https://www.facebook.com/profile.php?id=61588871683024",
    facebookHandle: "Orion Facilities Management",
  },
};
